<template>
  <div>
    <v-card>
      <v-toolbar color="transparent">
        <v-toolbar-title class="text-headline-medium">
          Uporabniki
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          prepend-icon="add"
          data-cy="create-new-user-button"
          @click="newUser.isDialogOpen = true"
        >
          Nov uporabnik
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <!-- Send login link dialog -->
        <v-dialog
          v-model="sendLoginLink.isDialogOpen"
          persistent
          max-width="600px"
          data-cy="send-login-link-dialog"
        >
          <v-card>
            <v-card-title>
              Pošlji prijavno povezavo
            </v-card-title>
            <v-card-text>
              Uporabnik bo na email prejel povezavo s katero se lahko avtomatsko prijavi v SiteLOG vašega podjetja.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                variant="text"
                @click="sendLoginLink.isDialogOpen=false"
              >
                Zapri
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                @click="sendLoginLinkViaEmail"
              >
                Pošlji email
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- New user dialogs -->
        <v-dialog
          v-model="newUser.isDialogOpen"
          persistent
          max-width="600px"
        >
          <v-card v-show="newUser.showSuccessMessage">
            <v-card-title>
              Dodaj uporabnika
            </v-card-title>

            <v-card-text>
              Uporabniku smo na e-mail poslali povezavo s katero se prijavi v aplikacijo.
            </v-card-text>

            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                variant="text"
                data-cy="close-new-user-dialog"
                @click="closeNewUserDialog"
              >
                Zapri
              </v-btn>
            </v-card-actions>
          </v-card>

          <v-card
            v-show="!newUser.showSuccessMessage"
            data-cy="new-user-dialog"
          >
            <v-card-title>
              Dodaj uporabnika
            </v-card-title>
            <v-card-text>
              <v-form
                ref="form"
              >
                <v-text-field
                  v-model="newUser.data.first_name"
                  label="Ime"
                  :rule="firstNameRule"
                />
                <v-text-field
                  v-model="newUser.data.last_name"
                  label="Priimek"
                  :rule="lastNameRule"
                />
                <v-text-field
                  v-model="newUser.data.email"
                  type="email"
                  label="Email"
                  :rules="emailRules"
                />
                <v-select
                  v-model="newUser.data.role"
                  label="Uporabniška vloga"
                  :items="available_roles"
                  :rules="userRoleRule"
                />
                <v-select
                  v-if="rolesWithProjectSelection.includes(newUser.data.role)"
                  v-model="newUser.data.access_to_projects"
                  label="Dostop do projektov"
                  chips
                  multiple
                  :items="availableProjects"
                />
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                variant="text"
                @click="newUser.isDialogOpen=false"
              >
                Zapri
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                @click="createNewUser"
              >
                Dodaj uporabnika
              </v-btn>
            </v-card-actions>
            <v-progress-linear
              v-if="newUser.inProgress"
              :indeterminate="true"
            />
          </v-card>
        </v-dialog>

        <!-- Edit user dialog -->
        <v-dialog
          v-model="editUser.isDialogOpen"
          persistent
          max-width="600px"
        >
          <v-card>
            <v-card-title>
              Uredi uporabnika
            </v-card-title>
            <v-card-text>
              <v-text-field
                v-model="editUser.data.display_name"
                disabled
                label="Naziv"
              />
              <v-select
                v-model="editUser.data.role"
                label="Uporabniška vloga"
                :items="available_roles"
              />
              <v-select
                v-if="rolesWithProjectSelection.includes(editUser.data.role)"
                v-model="editUser.data.access_to_projects"
                label="Dostop do projektov"
                chips
                multiple
                :items="availableProjects"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                variant="text"
                @click="editUser.isDialogOpen=false"
              >
                Zapri
              </v-btn>
              <v-btn
                color="primary"
                variant="outlined"
                @click="changeUserRole"
              >
                Spremeni vlogo uporabnika
              </v-btn>
            </v-card-actions>
            <v-progress-linear
              v-if="editUser.inProgress"
              :indeterminate="true"
            />
          </v-card>
        </v-dialog>

        <!-- Confirm user removal -->
        <v-dialog
          v-model="removeUser.isDialogOpen"
          persistent
          max-width="600px"
        >
          <v-card>
            <v-card-title>
              Odstrani uporabnika
            </v-card-title>
            <v-card-text>
              <v-text-field
                v-model="removeUser.data.display_name"
                disabled
                label="Naziv"
              />
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="primary"
                variant="text"
                @click="removeUser.isDialogOpen=false"
              >
                Zapri
              </v-btn>
              <v-btn
                color="error"
                @click="removeUserFromCompany(removeUser.data.user_id)"
              >
                Odstrani uporabnika
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-data-table
          id="list-of-users"
          hide-default-footer
          :items="users"
          :headers="headers"
          :items-per-page="-1"
        >
          <template
            #item.role="{ item }"
          >
            {{ $ucfirst($userRole(item.role)) }}
          </template>
          <template
            #item.actions="{ item }"
          >
            <v-btn
              icon
              variant="text"
              size="small"
              aria-label="Pošlji prijavno povezavo"
              data-cy="send-user-login-link"
              @click="openSendLoginLink(item.id)"
            >
              <v-icon>mail</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              aria-label="Uredi uporabnika"
              data-cy="edit-user"
              @click="openEditUserDialog(item.id)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
            <v-btn
              icon
              variant="text"
              size="small"
              aria-label="Odstrani uporabnika"
              data-cy="remove-user"
              @click="openRemoveUserDialog(item.id)"
            >
              <v-icon>delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <br>

        <v-table>
          <tbody>
            <tr>
              <td><b>Administrator</b></td>
              <td>Pregled in urejanje vseh projektov.</td>
            </tr>
            <tr>
              <td><b>Vodja projektov</b></td>
              <td>Pregled in urejanje projektov, ki so mu dodeljeni.</td>
            </tr>
            <tr>
              <td><b>Podizvajalec</b></td>
              <td>Uporabnik lahko ureja in dodaja dnevne liste na projektih, ki so mu dodeljeni. Uporabnik ne more urejati uvodnih listov.</td>
            </tr>
            <tr>
              <td><b>Pregledovalec</b></td>
              <td>Uporabnik lahko samo pregleduje projeke, ki so mu dodeljeni. Uporabnik ne more urejati uvodnih in dnevnih listov.</td>
            </tr>
            <tr>
              <td><b>Nadzornik</b></td>
              <td>Nadzornik lahko podpiše dnevni list in vnese opombe pod točke 6x.</td>
            </tr>
            <tr>
              <td><b>Notranja kontrola kakovosti</b></td>
              <td>Notranja kontrola kakovosti lahko na dnevni list vpisuje opombe pod točko 6x.</td>
            </tr>
            <tr>
              <td><b>Zunanja kontrola kakovosti</b></td>
              <td>Zunanja kontrola kakovosti lahko na dnevni list vpisuje opombe pod točko 6x.</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
  import { UserRole } from '../model/user/UserRole'

  export default {
    name: 'UsersView',
    data () {
      return {
        available_roles: [
          { title: 'Administrator', value: UserRole.ADMIN },
          { title: 'Vodja projektov', value: UserRole.PROJECT_LEAD },
          { title: 'Podizvajalec', value: UserRole.SUBCONTRACTOR },
          { title: 'Pregledovalec', value: UserRole.REVIEWER },
          { title: 'Nadzornik', value: UserRole.SUPERVISOR },
          { title: 'Notranja kontrola kakovosti', value: UserRole.NKK },
          { title: 'Zunanji kontrola kakovosti', value: UserRole.ZKK },
        ],
        rolesWithProjectSelection: [
          UserRole.PROJECT_LEAD,
          UserRole.SUBCONTRACTOR,
          UserRole.REVIEWER,
          UserRole.SUPERVISOR,
          UserRole.NKK,
          UserRole.ZKK
        ],
        newUser: {
          isDialogOpen: false,
          inProgress: false,
          showSuccessMessage: false,
          data: {
            first_name: '',
            last_name: '',
            email: '',
            role: '',
            access_to_projects: []
          },
        },
        editUser: {
          isDialogOpen: false,
          inProgress: false,
          data: {
            user_id: '',
            display_name: '',
            role: '',
            access_to_projects: []
          }
        },

        removeUser: {
          isDialogOpen: false,
          data: {
            user_id: '',
            display_name: ''
          }
        },

        sendLoginLink: {
          isDialogOpen: false,
          userId: null,
        },

        headers: [
          {
            title: 'Ime',
            key: 'first_name'
          },
          {
            title: 'Priimek',
            key: 'last_name'
          },
          {
            title: 'Email',
            key: 'email'
          },
          {
            title: 'Uporabniška vloga',
            key: 'role'
          },
          {
            title: 'Akcije',
            key: 'actions',
            sortable: false
          }
        ],
      };
    },
    computed: {
      users() {
        return this.$store.state.users;
      },
      firstNameRule() {
        return [
          v => !!v || 'Vnesite ime',
        ]
      },
      lastNameRule() {
        return [
          v => !!v || 'Vnesite priimek',
        ]
      },
      emailRules() {
        return [
          v => !!v || 'Vnos e-pošte je obvezen',
          v => /.+@.+/.test(v) || 'Vpišite veljaven e-poštni naslov',
          v => !this.users.some(u => u.email.trim().toLowerCase() === v.trim().toLowerCase()) || 'Uporabnik je že pridružen'
        ]
      },
      userRoleRule() {
        return [
          v => !!v || 'Izberite uporabniško vlogo',
        ]
      },
      availableProjects() {
        return this.$store.state.listOfProjects.map(p => {
          return { title: p.name, value: p.project_id };
        });
      }
    },
    async created() {
      this.$store.dispatch('fetchUsers');
      this.$store.dispatch('fetchProjects');
    },
    methods: {
      openEditUserDialog(userId) {
        const user = this.users.find(u => u.id === userId);
        this.editUser.data.user_id            = user.id;
        this.editUser.data.display_name       = `${user.first_name} ${user.last_name}`;
        this.editUser.data.role               = user.role;

        this.editUser.data.access_to_projects = this
          .$store
          .state
          .listOfProjects
          .filter(p => -1 !== (
              p.project_leads || [])
              .concat(p.project_subcontractors || [])
              .concat(p.project_reviewers || [])
              .concat(p.project_supervisors || [])
              .concat(p.project_nkks || [])
              .concat(p.project_zkks || [])
              .indexOf(userId))
          .map(p => ({ title: p.name, value: p.project_id }))
        ;

        this.editUser.isDialogOpen = true;
      },
      openSendLoginLink(userId) {
        this.sendLoginLink.userId = userId;
        this.sendLoginLink.isDialogOpen = true;
      },
      async sendLoginLinkViaEmail() {
        this.sendLoginLink.inProgress = true;

        await this.$store.dispatch('sendEmailLink', {
          userId: this.sendLoginLink.userId,
        })

        this.sendLoginLink.inProgress = false;
        this.sendLoginLink.isDialogOpen = false;
      },
      async createNewUser() {

        if ((await this.$refs.form.validate()).valid) {
          this.newUser.inProgress = true;

          await this.$store.dispatch('inviteUser', {
            companyId: this.$store.getters.currentCompany.company_id,
            firstName: this.newUser.data.first_name,
            lastName : this.newUser.data.last_name,
            email    : this.newUser.data.email,
            roleType : this.newUser.data.role
          });

          this.newUser.showSuccessMessage = true;
          this.newUser.inProgress         = false;

          await this.$store.dispatch('fetchUsers');

          const user = this.$store.state.users.find(u => u.email === this.newUser.data.email);
          const roleActions = {
              [UserRole.PROJECT_LEAD]: 'addProjectLead',
              [UserRole.SUBCONTRACTOR]: 'addProjectSubcontractor',
              [UserRole.REVIEWER]: 'addProjectReviewer',
              [UserRole.SUPERVISOR]: 'addProjectSupervisor',
              [UserRole.NKK]: 'addProjectNkks',
              [UserRole.ZKK]: 'addProjectZkks',
          };

          if (roleActions[this.newUser.data.role]) {
            this.newUser.data.access_to_projects.forEach(projectId => {
              if (projectId) {
                this.$store.dispatch(
                    roleActions[this.newUser.data.role],
                  { projectId: projectId, userId: user.id }
                );
              }
            });
          }
        }
      },
      clearNewUserDialog() {
        this.newUser.showSuccessMessage = false;
        this.newUser.data = {
          first_name: '',
          last_name: '',
          email: '',
          role: ''
        };
      },
      closeNewUserDialog() {
        this.newUser.isDialogOpen = false;
        this.clearNewUserDialog();
      },
      async changeUserRole() {
        this.editUser.isDialogOpen = false;
        this.editUser.inProgress   = true;

        await this.$store.dispatch('changeUserRole', {
          userId: this.editUser.data.user_id,
          roleType: this.editUser.data.role
        });

        await this.$store.dispatch('setUserProjectAccess', {
          userId:     this.editUser.data.user_id,
          userRole:   this.editUser.data.role,
          projectIds: this.editUser.data.access_to_projects,
        });

        this.editUser.inProgress = false;
      },
      openRemoveUserDialog(userId) {
        const user = this.users.find(u => u.id === userId);

        this.removeUser.data.user_id      = user.id;
        this.removeUser.data.display_name = `${user.first_name} ${user.last_name}`;
        this.removeUser.isDialogOpen      = true;
      },
      removeUserFromCompany(userId) {
        this.$store.dispatch('removeUserFromCompany', { userId });

        this.removeUser.isDialogOpen = false;
      }
    }
  }
</script>
